import React, { createContext, useCallback, useContext, useState } from 'react';

import { Alert, Box, Button, LinearProgress, Paper, Snackbar, Tooltip, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';

type Timestamp = string | number | Date;

interface RipProgressUpdate {
   clientId: string;
   message: string;
   progressPercentage: number;
   timestamp: Timestamp;
}

interface ScodixFileData {
   Layer: string;
   Uri: string;
}

interface RipFilesReady {
   clientId: string;
   scodixFileData: ScodixFileData[];
   uriCMYK: string;
   timestamp: Timestamp;
}

interface RipErrorMessage {
   clientId: string;
   error: string;
   timestamp: Timestamp;
}

interface SignalRState {
   progress: RipProgressUpdate | null;
   filesReady: RipFilesReady | null;
   error: RipErrorMessage | null;
   isConnected: boolean;
   connectionError: string | null;
   registrationError: string | null;
}

interface SignalRContextType extends SignalRState {
   clearProgress: () => void;
   clearError: () => void;
   clearFilesReady: () => void;
   clearConnectionError: () => void;
   clearRegistrationError: () => void;
   updateState: (updates: Partial<SignalRState>) => void;
}

const SignalRContext = createContext<SignalRContextType | undefined>(undefined);

export const SignalRProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
   const [state, setState] = useState<SignalRState>({
      progress: null,
      filesReady: null,
      error: null,
      isConnected: false,
      connectionError: null,
      registrationError: null,
   });

   const clearProgress = useCallback(() => {
      setState((prev) => ({ ...prev, progress: null }));
   }, []);

   const clearError = useCallback(() => {
      setState((prev) => ({ ...prev, error: null }));
   }, []);

   const clearFilesReady = useCallback(() => {
      setState((prev) => ({ ...prev, filesReady: null }));
   }, []);

   const clearConnectionError = useCallback(() => {
      setState((prev) => ({ ...prev, connectionError: null }));
   }, []);

   const clearRegistrationError = useCallback(() => {
      setState((prev) => ({ ...prev, registrationError: null }));
   }, []);

   const updateState = useCallback((updates: Partial<SignalRState>) => {
      setState((prev) => {
         // When an error is received, clear the progress
         if (updates.error) {
            return { ...prev, ...updates, progress: null };
         }
         return { ...prev, ...updates };
      });
   }, []);

   return (
      <SignalRContext.Provider
         value={{
            ...state,
            clearProgress,
            clearError,
            clearFilesReady,
            clearConnectionError,
            clearRegistrationError,
            updateState,
         }}
      >
         {children}
      </SignalRContext.Provider>
   );
};

export const useSignalRState = () => {
   const context = useContext(SignalRContext);
   if (context === undefined) {
      throw new Error('useSignalRState must be used within a SignalRProvider');
   }
   return context;
};

// Notification Component
export const SignalRNotifications: React.FC = () => {
   const location = useLocation();
   const {
      progress,
      filesReady,
      error,
      connectionError,
      registrationError,
      clearProgress,
      clearError,
      clearConnectionError,
   } = useSignalRState();
   const [registrationErrorSnackbarDismissed, setRegistrationErrorSnackbarDismissed] = React.useState(false);
   const isUploadPage = location.pathname === '/upload';

   // Reset dismissed state when a new registration error occurs
   React.useEffect(() => {
      if (registrationError) {
         setRegistrationErrorSnackbarDismissed(false);
      }
   }, [registrationError]);

   // Clear progress when error is displayed
   React.useEffect(() => {
      if (error && progress) {
         clearProgress();
      }
   }, [error, progress, clearProgress]);

   return (
      <>
         {progress && !isUploadPage && (
            <Paper
               elevation={3}
               sx={{
                  position: 'fixed',
                  top: 280,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 9999,
                  minWidth: 300,
                  maxWidth: '80vw', // Increase max width for longer text
                  p: 2,
                  backgroundColor: 'background.paper',
                  wordBreak: 'break-word', // Ensure long words break
               }}
            >
               <Typography
                  variant='body2'
                  gutterBottom
                  sx={{
                     whiteSpace: 'pre-wrap', // Preserve line breaks
                     wordBreak: 'break-word',
                  }}
               >
                  {progress.message}
               </Typography>

               <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Box sx={{ width: '100%', mr: 1 }}>
                     <LinearProgress
                        variant='determinate'
                        value={progress.progressPercentage}
                        sx={{ height: 8, borderRadius: 4 }}
                     />
                  </Box>
                  <Box sx={{ minWidth: 40 }}>
                     <Typography variant='body2' color='text.secondary'>
                        {`${Math.round(progress.progressPercentage)}%`}
                     </Typography>
                  </Box>
               </Box>
            </Paper>
         )}

         {/* RIP Processing Error Snackbar */}
         <Snackbar
            open={!!error}
            onClose={clearError}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            sx={{
               marginTop: '64px', // Direct margin on the Snackbar
            }}
         >
            <Alert onClose={clearError} severity='error' sx={{ width: '100%' }} variant='filled'>
               <Typography variant='subtitle1' fontWeight='bold'>
                  Processing Error
               </Typography>
               <Typography variant='body2'>{error?.error}</Typography>
            </Alert>
         </Snackbar>

         {/* Connection Error Snackbar */}
         <Snackbar
            open={!!connectionError}
            onClose={clearConnectionError}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            autoHideDuration={null}
            sx={{
               marginTop: '64px',
            }}
         >
            <Alert onClose={clearConnectionError} severity='error' sx={{ width: '100%' }} variant='filled'>
               <Typography variant='subtitle1' fontWeight='bold'>
                  Connection Lost
               </Typography>
               <Typography variant='body2'>{connectionError}</Typography>
            </Alert>
         </Snackbar>

         {/* Account already connected from another device Snackbar - dismiss hides it but uploads stay disabled */}
         <Snackbar
            open={!!registrationError && !registrationErrorSnackbarDismissed}
            onClose={() => setRegistrationErrorSnackbarDismissed(true)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            autoHideDuration={null}
            sx={{
               marginTop: '64px',
            }}
         >
            <Alert
               onClose={() => setRegistrationErrorSnackbarDismissed(true)}
               severity='error'
               sx={{ width: '100%' }}
               variant='filled'
            >
               <Typography variant='subtitle1' fontWeight='bold'>
                  Error
               </Typography>
               <Typography variant='body2'>{registrationError}</Typography>
            </Alert>
         </Snackbar>
      </>
   );
};

export type { RipProgressUpdate, RipFilesReady, RipErrorMessage };
